import RoleBasedGuard from 'guards/RoleBasedGuard';
import { lazy, Suspense } from 'react';
import { Navigate, useLocation, useRoutes } from 'react-router-dom';

import LoadingScreen from '../components/LoadingScreen';
import AuthGuard from '../guards/AuthGuard';
import GuestGuard from '../guards/GuestGuard';

// ----------------------------------------------------------------------

const Loadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();
  const isDashboard = pathname.includes('/dashboard');

  return (
    <Suspense
      fallback={
        <LoadingScreen
          sx={{
            ...(!isDashboard && {
              top: 0,
              left: 0,
              width: 1,
              zIndex: 9999,
              position: 'fixed'
            })
          }}
        />
      }
    >
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  return useRoutes([
    {
      path: 'app',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { path: 'dashboard', element: <Dashboard /> },
        { path: 'account', element: <Account /> },
        {
          path: 'products',
          children: [
            { element: <Navigate to="/app/products/list" replace /> },
            {
              path: 'list',
              element: <Products />
            },
            {
              path: 'new',
              element: (
                <RoleBasedGuard accessibleRoles={['ODIN', 'LOKI', 'THOR']}>
                  <CreateProduct />
                </RoleBasedGuard>
              )
            }
          ]
        },
        {
          path: 'warehouse',
          children: [
            { element: <Navigate to="/app/warehouse" replace /> },
            {
              path: 'list',
              element: (
                <RoleBasedGuard accessibleRoles={['ODIN', 'THOR', 'LOKI']}>
                  <Warehouse />
                </RoleBasedGuard>
              )
            }
          ]
        },
        {
          path: 'orders',
          children: [
            { element: <Navigate to="/app/orders/list" replace /> },
            { path: 'list', element: <Orders /> },
            {
              path: ':id/pick',
              element: (
                <RoleBasedGuard accessibleRoles={['ODIN', 'THOR']}>
                  <NewOrderPick />
                </RoleBasedGuard>
              )
            },
            // DEPRECATED PICK
            // {
            //   path: ':id/pick',
            //   element: (
            //     <RoleBasedGuard accessibleRoles={['ODIN', 'THOR']}>
            //       <OrderPick />
            //     </RoleBasedGuard>
            //   )
            // },
            {
              path: ':id/pack',
              element: (
                <RoleBasedGuard accessibleRoles={['ODIN', 'THOR']}>
                  <OrderPack />
                </RoleBasedGuard>
              )
            },
            { path: '/new', element: <CreateOrder /> },
            { path: ':id/detail', element: <OrderDetail /> },
            {
              path: 'multi-pick',
              element: <MultiPick />
            }
          ]
        },
        {
          path: 'cancel-orders',
          children: [
            { element: <Navigate to="/app/cancel-orders/list" replace /> },
            { path: 'list', element: <OrdersInCancelation /> },
            { path: ':id', element: <OrderCancelation /> }
          ]
        },
        {
          path: 'receptions',
          children: [
            { element: <Navigate to="/app/receptions/list" replace /> },
            {
              path: 'list',
              element: <Receptions />
            },
            { path: 'new', element: <CreateReception /> },
            {
              path: ':id/store',
              element: (
                <RoleBasedGuard accessibleRoles={['ODIN', 'THOR']}>
                  <StoreReception />
                </RoleBasedGuard>
              )
            }
          ]
        },
        {
          path: 'clients',
          children: [
            {
              element: (
                <RoleBasedGuard accessibleRoles={['ODIN']}>
                  <Clients />
                </RoleBasedGuard>
              )
            },
            { path: '/new', element: <CreateClient /> },
            { path: '/:id/settings', element: <ClientSettings /> }
          ]
        },
        {
          path: '/order-history',
          element: <OrdersHistory />
        },
        {
          path: 'reception-history',
          element: <ReceptionsHistory />
        },
        {
          path: 'recollect-orders',
          children: [
            {
              element: <Navigate to="/app/recollect-orders/list" replace />
            },
            { path: '/list', element: <RecollectOrders /> },
            { path: '/new', element: <CreateRecollectOrder /> },
            {
              path: '/:id/pick',
              element: (
                <RoleBasedGuard accessibleRoles={['ODIN', 'THOR']}>
                  <PickRecollectOrder />
                </RoleBasedGuard>
              )
            }
          ]
        },
        {
          path: 'recollect-history',
          element: <RecollectOrdersHistory />
        },
        {
          path: 'financial-state',
          element: <FinancialState />
        },
        {
          path: 'create-orders',
          element: <CreateOrder />
        },
        {
          path: 'locations',
          children: [
            { element: <Navigate to="/app/locations/list" replace /> },
            { path: '/list', element: <Locations /> },
            { path: ':id/cycle-count', element: <CycleCount /> }
          ]
        },
        {
          path: 'postal-codes',
          element: <PostalCodes />
        },
        {
          path: 'packaged',
          element: <Packaged />
        },
        {
          path: 'reports',
          element: <Reports />
        },
        {
          path: 'integrations',
          element: <Integrations />
        }
      ]
    },
    // Main Routes
    {
      path: '*',
      element: <MainLayout />,
      children: [
        { element: <Navigate to="/app/dashboard" replace /> },
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          )
        },
        { path: '404', element: <NotFound /> },
        { path: '*', element: <Navigate to="/404" replace /> }
      ]
    }
  ]);
}

// Import Components

// Layouts

const DashboardLayout = Loadable(
  lazy(() => import('../components/layout/DashboardLayout'))
);
const MainLayout = Loadable(
  lazy(() => import('../components/layout/MainLayout'))
);

// Pages
const Login = Loadable(lazy(() => import('../pages/Login')));
const Dashboard = Loadable(lazy(() => import('../pages/Dashboard')));
const Orders = Loadable(lazy(() => import('../pages/Orders/OrdersPage')));
const OrderDetail = Loadable(lazy(() => import('../pages/Orders/OrderDetail')));
const OrdersHistory = Loadable(lazy(() => import('../pages/OrdersHistory')));
// DEPRECATED PICK
// const OrderPick = Loadable(lazy(() => import('../pages/OrderPick')));
const OrderPack = Loadable(lazy(() => import('../pages/OrderPack')));
const Receptions = Loadable(lazy(() => import('../pages/Receptions')));
const ReceptionsHistory = Loadable(
  lazy(() => import('../pages/ReceptionsOrdersHistory'))
);
const CreateReception = Loadable(
  lazy(() => import('../pages/CreateReception'))
);
const StoreReception = Loadable(lazy(() => import('../pages/StoreReception')));
const CreateProduct = Loadable(lazy(() => import('../pages/CreateProduct')));
const Warehouse = Loadable(lazy(() => import('../pages/Warehouses')));
const Clients = Loadable(lazy(() => import('../pages/Clients')));
const CreateClient = Loadable(
  lazy(() => import('../pages/Clients/CreateClient'))
);
const ClientSettings = Loadable(
  lazy(() => import('../pages/Clients/ClientSettings'))
);
const Account = Loadable(lazy(() => import('../pages/Account')));
const NotFound = Loadable(lazy(() => import('../pages/NotFound')));
const CreateOrder = Loadable(
  lazy(() => import('../pages/Orders/CreateOrder/index.js'))
);
const Products = Loadable(lazy(() => import('../pages/Products')));
const RecollectOrders = Loadable(
  lazy(() => import('../pages/RecollectOrders'))
);
const CreateRecollectOrder = Loadable(
  lazy(() => import('../pages/RecollectOrders/CreateNewOrder'))
);
const RecollectOrdersHistory = Loadable(
  lazy(() => import('../pages/RecollectOrdersHistory'))
);
const PickRecollectOrder = Loadable(
  lazy(() => import('../pages/RecollectOrders/PickRecollectOrder'))
);
const FinancialState = Loadable(lazy(() => import('../pages/FinancialState')));
const OrderCancelation = Loadable(
  lazy(() => import('../pages/CanceledOrders/CancelationRequestProcess'))
);
const OrdersInCancelation = Loadable(
  lazy(() => import('../pages/CanceledOrders/CanceledOrdersList'))
);
const MultiPick = Loadable(lazy(() => import('../pages/MultiPick')));
const Locations = Loadable(lazy(() => import('../pages/Locations')));
const PostalCodes = Loadable(lazy(() => import('../pages/PostalCodes')));
const Packaged = Loadable(lazy(() => import('../pages/Packaged')));
const CycleCount = Loadable(lazy(() => import('../pages/CycleCount')));
const Reports = Loadable(lazy(() => import('../pages/Reports')));
const NewOrderPick = Loadable(lazy(() => import('../pages/NewOrderPick')));
const Integrations = Loadable(lazy(() => import('../pages/Integrations')));
